import { formatTourHomeLabel, type BookedTour } from './tour-home';

interface TourCalendarEvent {
  uid: string;
  startAt: string;
  endAt: string;
  summary: string;
  description: string;
}

function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\r\n|\n|\r/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,');
}

function formatIcsUtc(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`
  );
}

function foldIcsLine(line: string): string {
  const encoder = new TextEncoder();
  if (encoder.encode(line).length <= 75) return line;
  const chunks: string[] = [];
  let current = '';
  let currentBytes = 0;
  for (const char of line) {
    const size = encoder.encode(char).length;
    const max = chunks.length === 0 ? 75 : 74;
    if (current && currentBytes + size > max) {
      chunks.push(current);
      current = char;
      currentBytes = size;
    } else {
      current += char;
      currentBytes += size;
    }
  }
  if (current) chunks.push(current);
  return chunks.map((chunk, index) => (index === 0 ? chunk : ` ${chunk}`)).join('\r\n');
}

export function bookedTourCalendarEvent(tour: BookedTour, groupName: string): TourCalendarEvent {
  const home = formatTourHomeLabel(tour.unitNumber, tour.propertyName, tour.building);
  return {
    uid: tour.bookingId,
    startAt: tour.startAt,
    endAt: tour.endAt,
    summary: `Amber Peak, ${home}`,
    description: `15-minute tour of ${home}. Shared with ${groupName}.`,
  };
}

export function buildTourCalendarIcs(events: TourCalendarEvent[]): string {
  const now = formatIcsUtc(new Date().toISOString());
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Amber Peak//Property Tours//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escapeIcsText(events.length === 1 ? events[0].summary : 'Amber Peak tours')}`,
  ];
  for (const event of events) {
    lines.push(
      'BEGIN:VEVENT',
      `UID:${escapeIcsText(event.uid)}@amberpeak.ca`,
      `DTSTAMP:${now}`,
      `DTSTART:${formatIcsUtc(event.startAt)}`,
      `DTEND:${formatIcsUtc(event.endAt)}`,
      `SUMMARY:${escapeIcsText(event.summary)}`,
      `DESCRIPTION:${escapeIcsText(event.description)}`,
      'END:VEVENT',
    );
  }
  lines.push('END:VCALENDAR');
  return `${lines.map(foldIcsLine).join('\r\n')}\r\n`;
}

export function downloadIcs(filename: string, contents: string): void {
  const blob = new Blob([contents], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function calendarFilename(tours: BookedTour[]): string {
  if (tours.length !== 1) return 'Amber Peak tours.ics';
  const home = formatTourHomeLabel(tours[0].unitNumber, tours[0].propertyName, tours[0].building);
  return `Amber Peak, ${home}.ics`;
}

export function downloadBookedTourCalendar(tours: BookedTour[], groupName: string): void {
  if (tours.length === 0) return;
  const events = tours.map((tour) => bookedTourCalendarEvent(tour, groupName));
  downloadIcs(calendarFilename(tours), buildTourCalendarIcs(events));
}

